﻿<script>
	import { tick } from "svelte";
	import { validator } from "../../validation";
	import { nanoid } from "nanoid";
	import { monthListOptions, yearListOptions } from "../../config/dateOptions";

	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import NumberInput from "../../components/NumberInput.svelte";
	import Select from "../../components/Select.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import PrimaryButton from "../../components/buttons/PrimaryButton.svelte";
	import Checkbox from "../../components/Checkbox.svelte";
	import CheckboxButtonList from "../../components/CheckboxButtonList.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import TextInput from "../../components/TextInput.svelte";

	export let id = nanoid(6);
	export let socialMedialDetailsData = {};
	export let platformLookupId = null;
	export let showOtherButton = false;
	export let otherTextValue = null;
	export let label = null;
	export let disabled = false;

	function hasValue(value) {
		if (value == null || value === false) return false;
		if (typeof value === "string") return value.trim().length > 0;
		if (Array.isArray(value)) return value.length > 0;
		if (typeof value === "object") return Object.values(value).some(hasValue);
		return true;
	}

	function hasSocialMediaDetails(data) {
		return Object.values(data ?? {}).some(hasValue);
	}

	function hasOtherText(value) {
		return typeof value === "string" && value.trim().length > 0;
	}

	// Determine whether to show the social media details section based on whether the platform is selected or if the "Other" text has a value.
	let showDetails =
		Number(socialMedialDetailsData?.socialMediaPlatformLookupId) === Number(platformLookupId) ||
			(showOtherButton && hasOtherText(otherTextValue));


	let checkboxButtonListSelected = [];

	$: buttonList = [{ 
		lookupName: label, 
		id: Number(platformLookupId) 
	}];

	// Make sure the CheckboxButtonList is selected if the social media details are already populated for this platform or if the "Other" text has a value.
	$: checkboxButtonListSelected =
		Number(socialMedialDetailsData?.socialMediaPlatformLookupId) === Number(platformLookupId) ||
		(showOtherButton && hasOtherText(otherTextValue))
			? [Number(platformLookupId)]
			: [];

	// Handle the change event from the CheckboxButtonList to update the social media details data and show/hide the details section.
	function handlePlatformChange(event) {
		const isSelected = event.target.checked;
		const updated = { ...socialMedialDetailsData };

		if (isSelected) {
			updated.socialMediaPlatformLookupId = Number(platformLookupId);
		} else {
			delete updated.socialMediaPlatformLookupId;
		}

		socialMedialDetailsData = updated;
		showDetails = isSelected;

		if (showOtherButton && !isSelected) {
			otherTextValue = null;
		}
	}

	async function handleEndDontKnowChange(event) {
		if (!event.target.checked) return;

		socialMedialDetailsData.stillUsing = false;

		// Validate the end year field if the "I don't know the year" checkbox was checked.
		// This will ensure the error message is cleared if it remains.
		if (event.target.id.startsWith("socialMedia-endYearDontKnow-")) {
			await tick();	// Wait for the DOM to update before validating the end year field.

			$validator.clearVisualError(
				document.getElementById(`socialMedia-endYear-${id}`),
				{ force: true }
			);
		}
	}

	async function handleStillUsingChange(event) {
		if (!event.target.checked) return;

		socialMedialDetailsData.endMonthDontKnow = false;
		socialMedialDetailsData.endYearDontKnow = false;

		await tick();

		$validator.clearVisualError(
			document.getElementById(`socialMedia-endMonth-${id}`),
			{ force: true }
		);

		$validator.clearVisualError(
			document.getElementById(`socialMedia-endYear-${id}`),
			{ force: true }
		);
	}

	$: if (!socialMedialDetailsData) {
		socialMedialDetailsData = {};
	}

	$: {
	if (socialMedialDetailsData.startMonthDontKnow &&
			socialMedialDetailsData.startMonth !== null) {
			socialMedialDetailsData.startMonth = null;
		}
		if (socialMedialDetailsData.startYearDontKnow &&
			socialMedialDetailsData.startYear !== null) {
			socialMedialDetailsData.startYear = null;
		}
		if ((socialMedialDetailsData.endMonthDontKnow || socialMedialDetailsData.stillUsing) &&
			socialMedialDetailsData.endMonth !== null) {
			socialMedialDetailsData.endMonth = null;
		}
		if ((socialMedialDetailsData.endYearDontKnow || socialMedialDetailsData.stillUsing) &&
			socialMedialDetailsData.endYear !== null) {
			socialMedialDetailsData.endYear = null;
		}
	}

	// Also open details if populated values arrive after initialization.
	$: if (
		Number(socialMedialDetailsData?.socialMediaPlatformLookupId) === Number(platformLookupId) ||
			(showOtherButton && hasOtherText(otherTextValue))
	) {
		showDetails = true;
	}

</script>
<FormRow style="margin-top:0px;">
	<FormColumn>
		<CheckboxButtonList
			{disabled}
			llllabel={label}
			bind:selected={checkboxButtonListSelected}
			optionList={buttonList}
			name={`socialMediaPlatforms-${id}`}
			on:change={handlePlatformChange}
			vvvvvalidator="required"
			vvvvalidatorValue="true"
			vvvvalidatorMessage="Please select at least one social media platform."
			validatorTarget={`socialMediaPlatforms-${id}`}
		/>
	</FormColumn>	
</FormRow>

{#if showDetails}

	{#if showOtherButton}
		<FormRow>
			<FormColumn>
				<TextArea
					{disabled}
					label="Please specify"
					bind:value={otherTextValue}
					autoExpand={true}
					rows={3}
					name="otherSocialMediaPlatforms"
					id={`otherSocialMediaPlatforms-${id}`} />
			</FormColumn>
		</FormRow>
	{:else}
		<FormRow>
			<FormColumn>
				<p><strong>What period did you use {label}?</strong></p>
				<p>If you're unsure of the exact date, you can select "I don't know"
			</FormColumn>
		</FormRow>

		<FormRow>
			<FormColumn cols="4">
				<div class="marginBottom10">
					<Select
						disabled={disabled || socialMedialDetailsData.startMonthDontKnow}
						label="Start Month"
						bind:value={socialMedialDetailsData.startMonth}
						optionList={monthListOptions}
						headOptionText="Select a month"
						id={`socialMedia-startMonth-${id}`}
					/>
				</div>
				<Checkbox
					{disabled}
					label="I don't know the month"
					bind:checked={socialMedialDetailsData.startMonthDontKnow}
					id={`socialMedia-startMonthDontKnow-${id}`}
				/>
			</FormColumn>
			<FormColumn cols="4">
				<div class="marginBottom10">
					<Select
						disabled={disabled || socialMedialDetailsData.startYearDontKnow}
						label="Start Year"
						bind:value={socialMedialDetailsData.startYear}
						optionList={yearListOptions}
						headOptionText="Select a year"
						id={`socialMedia-startYear-${id}`}
					/>
				</div>
				<Checkbox
					{disabled}
					label="I don't know the year"
					bind:checked={socialMedialDetailsData.startYearDontKnow}
					id={`socialMedia-startYearDontKnow-${id}`}
				/>
			</FormColumn>
		</FormRow>

		<FormRow class="d-flex align-items-center">
			<FormColumn cols="4">
				<div class="marginBottom10">
					<Select
						disabled={disabled || socialMedialDetailsData.endMonthDontKnow || socialMedialDetailsData.stillUsing}
						label="End Month"
						bind:value={socialMedialDetailsData.endMonth}
						optionList={monthListOptions}
						headOptionText="Select a month"
						id={`socialMedia-endMonth-${id}`}
					/>
				</div>
				<Checkbox
					{disabled}
					label="I don't know the month"
					bind:checked={socialMedialDetailsData.endMonthDontKnow}
					on:change={handleEndDontKnowChange}
					id={`socialMedia-endMonthDontKnow-${id}`}
				/>
			</FormColumn>
			<FormColumn cols="4">
				<div class="marginBottom10">
					<Select
						disabled={disabled || socialMedialDetailsData.endYearDontKnow || socialMedialDetailsData.stillUsing}
						label="End Year"
						bind:value={socialMedialDetailsData.endYear}
						optionList={yearListOptions}
						headOptionText="Select a year"
						validator="socialMediaEndDate"
						id={`socialMedia-endYear-${id}`}
					/>
				</div>
				<Checkbox
					{disabled}
					label="I don't know the year"
					bind:checked={socialMedialDetailsData.endYearDontKnow}
					on:change={handleEndDontKnowChange}
					id={`socialMedia-endYearDontKnow-${id}`}
				/>
			</FormColumn>
			<FormColumn cols="4">
				<Checkbox
					{disabled}
					label="Still Using"
					bind:checked={socialMedialDetailsData.stillUsing}
					on:change={handleStillUsingChange}
					id={`socialMedia-stillUsing-${id}`}
				/>
			</FormColumn>
		</FormRow>
		<FormRow style="margin-bottom:36px;">
			<FormColumn>
				<TextInput
					required
					{disabled}
					label="Please enter your username(s) associated with your account"
					bind:value={socialMedialDetailsData.userName}
					maxlength="255"
					name="socialMediaUserName"
					id={`socialMediaUserName-${id}`} />
			</FormColumn>
		</FormRow>
	{/if}
{/if}

<style>
	.marginBottom10 {
		margin-bottom: 10px;
	}
</style>